import { ExperienceAPI, SQLExecutionResponse } from './experienceAPI';

export interface ChartData {
  id: string;
  title: string;
  chartType: string;
  data: any[];
  xField?: string;
  yField?: string;
  colorField?: string;
  error?: string;
  loading: boolean;
}

export interface DashboardChart {
  id: string;
  title: string;
  chart_type: string;
  description: string;
  sql_query: string;
  columns: string[];
  x_axis?: string;
  y_axis?: string;
  config: any;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface Dashboard {
  id: string;
  title: string;
  description: string;
  charts: DashboardChart[];
  layout: any;
}

export interface DashboardsResponse {
  file_id: string;
  dashboards: Dashboard[];
  status: string;
  message: string;
  success: boolean;
}

class ChartDataService {
  private static instance: ChartDataService;
  private chartDataCache: Map<string, ChartData> = new Map();

  static getInstance(): ChartDataService {
    if (!ChartDataService.instance) {
      ChartDataService.instance = new ChartDataService();
    }
    return ChartDataService.instance;
  }

  /**
   * Fetch data for a single chart by executing its SQL query
   */
  async fetchChartData(
    fileId: string,
    chart: DashboardChart,
    onProgress?: (chartId: string, loading: boolean) => void
  ): Promise<ChartData> {
    const cacheKey = `${fileId}-${chart.id}`;
    
    // Check cache first
    if (this.chartDataCache.has(cacheKey)) {
      return this.chartDataCache.get(cacheKey)!;
    }

    // Initialize chart data
    const chartData: ChartData = {
      id: chart.id,
      title: chart.title,
      chartType: chart.chart_type,
      data: [],
      loading: true,
      xField: chart.x_axis,
      yField: chart.y_axis
    };

    // Update progress
    onProgress?.(chart.id, true);

    try {
      // Execute SQL query
      const sqlResponse: SQLExecutionResponse = await ExperienceAPI.executeSQL(
        fileId,
        chart.sql_query
      );

      if (sqlResponse.success && sqlResponse.results) {

        // Transform data based on chart type
        const transformedData = this.transformDataForChartType(
          sqlResponse.results,
          chart.chart_type,
          chart.columns
        );

        chartData.data = transformedData;
        chartData.loading = false;

        // Extract field names if not provided
        if (!chartData.xField || !chartData.yField) {
          const fields = this.extractChartFields(transformedData, chart.chart_type);
          chartData.xField = chartData.xField || fields.xField;
          chartData.yField = chartData.yField || fields.yField;
          chartData.colorField = chartData.colorField || fields.colorField;
        }

      } else {
        throw new Error((sqlResponse as any).message || 'SQL execution failed');
      }
    } catch (error) {
      console.error(`❌ Failed to fetch data for chart ${chart.title}:`, error);
      chartData.error = error instanceof Error ? error.message : 'Unknown error';
      chartData.loading = false;
    }

    // Cache the result
    this.chartDataCache.set(cacheKey, chartData);
    
    // Update progress
    onProgress?.(chart.id, false);

    return chartData;
  }

  /**
   * Fetch data for all charts in a dashboard
   */
  async fetchDashboardData(
    fileId: string,
    dashboard: Dashboard,
    onProgress?: (chartId: string, loading: boolean) => void
  ): Promise<ChartData[]> {
    const chartDataPromises = dashboard.charts.map(chart =>
      this.fetchChartData(fileId, chart, onProgress)
    );

    try {
      const chartDataArray = await Promise.all(chartDataPromises);
      return chartDataArray;
    } catch (error) {
      console.error(`❌ Failed to fetch dashboard data:`, error);
      throw error;
    }
  }

  /**
   * Transform SQL results based on chart type
   */
  private transformDataForChartType(
    results: any[],
    chartType: string,
    columns: string[]
  ): any[] {
    if (!results || results.length === 0) {
      return [];
    }

    switch (chartType) {
      case 'pie':
        return this.transformForPieChart(results, columns);
      case 'bar':
        return this.transformForBarChart(results, columns);
      case 'line':
        return this.transformForLineChart(results, columns);
      case 'histogram':
        return this.transformForHistogram(results, columns);
      case 'table':
        return results; // Keep as is for tables
      case 'scatter':
        return this.transformForScatterChart(results, columns);
      case 'area':
        return this.transformForAreaChart(results, columns);
      default:
        return this.transformForBarChart(results, columns); // Default fallback
    }
  }

  /**
   * Transform data for pie charts
   */
  private transformForPieChart(results: any[], columns: string[]): any[] {
    if (columns.length >= 2) {
      return results.map(row => ({
        category: row[columns[0]],
        value: row[columns[1]] || row['count'] || 1
      }));
    }
    return results;
  }

  /**
   * Transform data for bar charts
   */
  private transformForBarChart(results: any[], columns: string[]): any[] {
    if (columns.length >= 2) {
      return results.map(row => ({
        category: row[columns[0]],
        value: row[columns[1]] || row['count'] || 1
      }));
    }
    return results;
  }

  /**
   * Transform data for line charts
   */
  private transformForLineChart(results: any[], columns: string[]): any[] {
    if (columns.length >= 2) {
      return results.map(row => ({
        x: row[columns[0]],
        y: row[columns[1]] || row['count'] || 1
      }));
    }
    return results;
  }

  /**
   * Transform data for histogram charts
   */
  private transformForHistogram(results: any[], columns: string[]): any[] {
    if (columns.length >= 1) {
      return results.map(row => ({
        value: row[columns[0]]
      }));
    }
    return results;
  }

  /**
   * Transform data for scatter charts
   */
  private transformForScatterChart(results: any[], columns: string[]): any[] {
    if (columns.length >= 2) {
      return results.map(row => ({
        x: row[columns[0]],
        y: row[columns[1]]
      }));
    }
    return results;
  }

  /**
   * Transform data for area charts
   */
  private transformForAreaChart(results: any[], columns: string[]): any[] {
    if (columns.length >= 2) {
      return results.map(row => ({
        x: row[columns[0]],
        y: row[columns[1]] || row['count'] || 1
      }));
    }
    return results;
  }

  /**
   * Extract field names from transformed data
   */
  private extractChartFields(data: any[], chartType: string): {
    xField?: string;
    yField?: string;
    colorField?: string;
  } {
    if (!data || data.length === 0) {
      return {};
    }

    const firstItem = data[0];
    const fields = Object.keys(firstItem);

    switch (chartType) {
      case 'pie':
        return {
          xField: fields.find(f => ['category', 'name', 'label'].includes(f)) || fields[0],
          yField: fields.find(f => ['value', 'count', 'amount'].includes(f)) || fields[1]
        };
      case 'bar':
        return {
          xField: fields.find(f => ['category', 'name', 'label'].includes(f)) || fields[0],
          yField: fields.find(f => ['value', 'count', 'amount'].includes(f)) || fields[1]
        };
      case 'line':
      case 'area':
        return {
          xField: fields.find(f => ['x', 'date', 'time'].includes(f)) || fields[0],
          yField: fields.find(f => ['y', 'value', 'count'].includes(f)) || fields[1]
        };
      case 'scatter':
        return {
          xField: fields.find(f => ['x'].includes(f)) || fields[0],
          yField: fields.find(f => ['y'].includes(f)) || fields[1]
        };
      case 'histogram':
        return {
          xField: fields.find(f => ['value', 'bin'].includes(f)) || fields[0]
        };
      default:
        return {
          xField: fields[0],
          yField: fields[1]
        };
    }
  }

  /**
   * Clear cache for a specific file
   */
  clearCache(fileId: string): void {
    const keysToDelete: string[] = [];
    this.chartDataCache.forEach((value, key) => {
      if (key.startsWith(fileId)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.chartDataCache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.chartDataCache.clear();
  }
}

export default ChartDataService;
